import React, { useEffect, useRef } from 'react';
import type { RelationshipGraph as GraphType } from '../../types/relationship';

interface Props {
  graph: GraphType;
  onNodeClick?: (nodeId: string) => void;
}

const RelationshipGraph: React.FC<Props> = ({ graph, onNodeClick }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!canvasRef.current || !containerRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const container = containerRef.current;
    canvas.width = container.clientWidth;
    canvas.height = 600;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Calculate node positions (force-directed layout simulation)
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = Math.min(canvas.width, canvas.height) / 3;

    // Position center guest
    const nodePositions = new Map<string, { x: number; y: number }>();
    nodePositions.set(graph.center_guest.id, { x: centerX, y: centerY });

    // Position other nodes in a circle
    const otherNodes = graph.nodes.filter(n => n.id !== graph.center_guest.id);
    otherNodes.forEach((node, index) => {
      const angle = (2 * Math.PI * index) / otherNodes.length;
      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);
      nodePositions.set(node.id, { x, y });
    });

    // Draw edges
    graph.edges.forEach(edge => {
      const pos1 = nodePositions.get(edge.guest1_id);
      const pos2 = nodePositions.get(edge.guest2_id);

      if (pos1 && pos2) {
        ctx.beginPath();
        ctx.moveTo(pos1.x, pos1.y);
        ctx.lineTo(pos2.x, pos2.y);

        // Color by strength
        switch (edge.strength) {
          case 'STRONG':
            ctx.strokeStyle = '#ef4444'; // red
            ctx.lineWidth = 3;
            break;
          case 'MEDIUM':
            ctx.strokeStyle = '#f59e0b'; // amber
            ctx.lineWidth = 2;
            break;
          case 'WEAK':
            ctx.strokeStyle = '#94a3b8'; // slate
            ctx.lineWidth = 1;
            break;
        }

        ctx.stroke();

        // Draw shared stays count
        const midX = (pos1.x + pos2.x) / 2;
        const midY = (pos1.y + pos2.y) / 2;
        ctx.fillStyle = '#64748b';
        ctx.font = '10px sans-serif';
        ctx.fillText(edge.shared_stays.toString(), midX, midY);
      }
    });

    // Draw nodes
    graph.nodes.forEach(node => {
      const pos = nodePositions.get(node.id);
      if (!pos) return;

      const isCenter = node.id === graph.center_guest.id;
      const nodeRadius = isCenter ? 25 : 15;

      // Draw circle
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, nodeRadius, 0, 2 * Math.PI);
      ctx.fillStyle = isCenter ? '#3b82f6' : '#60a5fa'; // blue
      ctx.fill();
      ctx.strokeStyle = '#1e40af';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Draw label
      ctx.fillStyle = '#1f2937';
      ctx.font = isCenter ? 'bold 12px sans-serif' : '11px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(
        node.full_name?.split(' ')[0] || node.full_name || 'Unknown', // First name only with fallback
        pos.x,
        pos.y + nodeRadius + 15
      );
    });

    // Add click handler
    const handleClick = (event: MouseEvent) => {
      if (!onNodeClick) return;

      const rect = canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      // Check if click is on a node
      for (const [nodeId, pos] of nodePositions.entries()) {
        const isCenter = nodeId === graph.center_guest.id;
        const nodeRadius = isCenter ? 25 : 15;
        const distance = Math.sqrt((x - pos.x) ** 2 + (y - pos.y) ** 2);

        if (distance <= nodeRadius) {
          onNodeClick(nodeId);
          break;
        }
      }
    };

    canvas.addEventListener('click', handleClick);

    return () => {
      canvas.removeEventListener('click', handleClick);
    };
  }, [graph, onNodeClick]);

  return (
    <div ref={containerRef} className="w-full">
      <canvas
        ref={canvasRef}
        className="border border-gray-200 rounded-lg cursor-pointer"
      />
      
      {/* Legend */}
      <div className="mt-4 flex items-center justify-center space-x-6 text-sm">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-0.5 bg-red-500"></div>
          <span className="text-gray-600">Strong</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-8 h-0.5 bg-amber-500"></div>
          <span className="text-gray-600">Medium</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-8 h-0.5 bg-slate-400"></div>
          <span className="text-gray-600">Weak</span>
        </div>
      </div>

      {/* Graph Info */}
      <div className="mt-4 grid grid-cols-3 gap-4 text-center">
        <div>
          <div className="text-2xl font-bold text-blue-600">{graph.total_nodes}</div>
          <div className="text-sm text-gray-600">Nodes</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-blue-600">{graph.total_edges}</div>
          <div className="text-sm text-gray-600">Connections</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-blue-600">{graph.max_degree}</div>
          <div className="text-sm text-gray-600">Max Degree</div>
        </div>
      </div>
    </div>
  );
};

export default RelationshipGraph;
